import queryString from "query-string";
import { useCallback, useMemo } from "react";
import useSWRInfinite from "swr/infinite";

import { File, Folder, CloudType } from "@/types/cloud";
import { typedFile } from "@/utils/typedFile";

const getCloudKey =
  (folderId: number, searchValue?: string, types?: CloudType[], tag?: number) =>
  (pageIndex, previousPageData) => {
    if ((previousPageData && !previousPageData?.data?.length) || !folderId)
      return null;

    const apiUrl = `/cloud/dir/${folderId}`;
    const queryParams = queryString.stringify({
      take: 8,
      page: pageIndex,
      search: searchValue,
      types,
      tag,
    });

    return `${apiUrl}?${queryParams}`;
  };

export const useCloud = (
  folderId: number,
  searchValue?: string,
  types?: CloudType[],
  tag?: number
): {
  folders: Folder[];
  files: File[];
  parentFolderId: number;
  parentFolder: Folder;
  isLoading?: boolean;
  hasNextPage?: boolean;
  loadMore?: () => void;
  error?: any;
  mutate?: () => void;
} => {
  const getKey = getCloudKey(folderId, searchValue, types, tag);
  const { data: pages, setSize, error, mutate } = useSWRInfinite(getKey);
  const isLoading = !pages && !error;

  const loadMore = useCallback(() => {
    setSize((size) => size + 1);
  }, [setSize]);

  const foldersData = useMemo(() => {
    if (pages?.[0])
      return {
        folders: pages?.[0]?.folders,
        parentFolderId: pages?.[0]?.parentFolderId,
        parentFolder: pages?.[0]?.parentFolder,
      };
    else return null;
  }, [pages]);

  const files = useMemo(() => {
    if (Array.isArray(pages))
      return pages
        ?.map((page) => page?.data?.map((file) => typedFile(file)))
        ?.flat();
    else return [];
  }, [pages]);

  const hasNextPage =
    pages?.length > 0 ? !!pages[pages.length - 1].nextPage : false;

  if (!pages)
    return {
      error,
      isLoading,
      hasNextPage,
      folders: null,
      parentFolderId: null,
      parentFolder: null,
      files: null,
      mutate,
    };

  return {
    folders: foldersData?.folders,
    parentFolderId: foldersData?.parentFolderId,
    parentFolder: foldersData?.parentFolder,
    files,
    hasNextPage,
    isLoading,
    loadMore,
    error,
    mutate,
  };
};
